import React, { useMemo, useState } from 'react';
import {
	Box,
	Button,
	Card,
	CardContent,
	Divider,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import NotesIcon from '@mui/icons-material/Notes';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditableTextSection from './EditableTextSection';
import JobFiles from './JobFiles';
import JobSchedulePanel from './JobSchedulePanel';
import JobCostsEditor from '../../../../Components/JobCosts/JobCostsEditor';
import ChangeOrderModal from '../../../../Components/Modals/ChangeOrderModal';
import SectionHeader from './SectionHeader';
import { surfaceCard } from '../../../../Shared/surfaceCard';
import {
	formatLineItemCurrency,
	lineItemTotal,
	lineItemsGrandTotal,
} from '../../../../Shared/lineItems';
import { useBidActions } from '../../../../Shared/bids';

const QuotedLineItemCard = ({ row }) => (
	<Card variant="outlined" sx={{ borderRadius: 2 }}>
		<CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
			<Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
				{row.description || row.name || '—'}
			</Typography>
			<Stack direction="row" justifyContent="space-between" spacing={1}>
				<Typography variant="caption" color="text.secondary">
					{row.quantity} × {formatLineItemCurrency(row.unit_price)}
				</Typography>
				<Typography variant="body2" fontWeight={700}>
					{formatLineItemCurrency(lineItemTotal(row))}
				</Typography>
			</Stack>
		</CardContent>
	</Card>
);

const QuotedLineItemsTable = ({ items, quoteIncludesStripeFee, amount }) => {
	const theme = useTheme();
	const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
	const rows = items || [];
	const netTotal = lineItemsGrandTotal(rows);
	const totalLabel = quoteIncludesStripeFee
		? 'Quoted total (incl. card fees)'
		: 'Quoted total';
	const totalValue = formatLineItemCurrency(amount ?? netTotal);

	if (!rows.length) {
		return (
			<Typography
				variant="body2"
				color="text.secondary"
				sx={{ px: { xs: 1.5, sm: 2 }, py: 2 }}
			>
				No quoted line items on the current bid. Use Create Bid or Create Change
				Order to send an itemized proposal.
			</Typography>
		);
	}

	if (!isDesktop) {
		return (
			<Box sx={{ px: { xs: 1.5, sm: 2 }, pb: 1.5 }}>
				<Stack spacing={1.25}>
					{rows.map((row) => (
						<QuotedLineItemCard
							key={row.id || `${row.description}-${row.quantity}`}
							row={row}
						/>
					))}
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'baseline',
							pt: 0.5,
							px: 0.25,
						}}
					>
						<Typography variant="body2" fontWeight={700}>
							{totalLabel}
						</Typography>
						<Typography variant="subtitle1" fontWeight={700}>
							{totalValue}
						</Typography>
					</Box>
				</Stack>
			</Box>
		);
	}

	return (
		<Box sx={{ px: { xs: 1, sm: 1.5 }, pb: 1.5 }}>
			<TableContainer sx={{ overflowX: 'auto' }}>
				<Table size="small" sx={{ minWidth: 360 }}>
					<TableHead>
						<TableRow>
							<TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
							<TableCell align="right" sx={{ fontWeight: 700, width: 72 }}>
								Qty
							</TableCell>
							<TableCell align="right" sx={{ fontWeight: 700, width: 100 }}>
								Rate
							</TableCell>
							<TableCell align="right" sx={{ fontWeight: 700, width: 100 }}>
								Total
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{rows.map((row) => (
							<TableRow key={row.id || `${row.description}-${row.quantity}`}>
								<TableCell sx={{ wordBreak: 'break-word' }}>
									{row.description || row.name || '—'}
								</TableCell>
								<TableCell align="right">{row.quantity}</TableCell>
								<TableCell align="right">
									{formatLineItemCurrency(row.unit_price)}
								</TableCell>
								<TableCell align="right">
									{formatLineItemCurrency(lineItemTotal(row))}
								</TableCell>
							</TableRow>
						))}
						<TableRow>
							<TableCell colSpan={3} align="right" sx={{ fontWeight: 700 }}>
								{totalLabel}
							</TableCell>
							<TableCell align="right" sx={{ fontWeight: 700 }}>
								{totalValue}
							</TableCell>
						</TableRow>
					</TableBody>
				</Table>
			</TableContainer>
		</Box>
	);
};

const JobMainContent = ({
	jobId,
	selectedJob,
	isEditingNotes,
	onNotesToggle,
	onChange,
	onNotesConfirm,
	onJobUpdated,
}) => {
	const theme = useTheme();
	const isDesktopLayout = useMediaQuery(theme.breakpoints.up('lg'));
	const [changeOrderOpen, setChangeOrderOpen] = useState(false);
	const bid = selectedJob?.bid_details;
	const { canCreateChangeOrder } = useBidActions(bid, {
		depositPaidOnJob: selectedJob?.deposit_paid_amount,
	});
	const quotedItems = bid?.itemized_pricing ? bid?.line_items || [] : [];
	const costsTotal = Number(selectedJob?.costs_total) || 0;
	const margin = selectedJob?.margin;
	const bidAmount = Number(bid?.amount ?? selectedJob?.bid_amount) || 0;

	const marginLabel = useMemo(() => {
		if (margin == null) return null;
		return formatLineItemCurrency(margin);
	}, [margin]);

	return (
		<Stack spacing={{ xs: 2.5, md: 3 }} sx={{ minWidth: 0 }}>
			{!isDesktopLayout ? (
				<EditableTextSection
					icon={NotesIcon}
					title="Notes"
					value={selectedJob.notes}
					placeholder="No notes provided."
					isEditing={isEditingNotes}
					onToggleEdit={onNotesToggle}
					onChange={(v) => onChange(v, 'notes')}
					onConfirm={onNotesConfirm}
				/>
			) : null}

			<Box sx={surfaceCard(theme)}>
				<SectionHeader
					icon={ReceiptLongIcon}
					title="Quoted Items"
					action={
						canCreateChangeOrder ? (
							<Button
								size="small"
								variant="outlined"
								startIcon={<RefreshIcon />}
								onClick={() => setChangeOrderOpen(true)}
								sx={{ textTransform: 'none', flexShrink: 0 }}
							>
								Change Order
							</Button>
						) : null
					}
				/>
				<Typography
					variant="body2"
					color="text.secondary"
					sx={{ px: { xs: 1.5, sm: 2 }, pt: 0.5, pb: 1 }}
				>
					{bid?.itemized_pricing
						? 'These charges are on the current bid sent to the customer. To change pricing, create a change order.'
						: bid
							? 'This bid uses a single project total. Create a change order with itemized pricing if you want line-by-line charges.'
							: 'No bid yet. Use Create Bid to send a proposal — itemized or lump sum.'}
				</Typography>
				<Divider sx={{ opacity: 0.6 }} />
				{bid?.itemized_pricing ? (
					<QuotedLineItemsTable
						items={quotedItems}
						quoteIncludesStripeFee={bid?.quote_includes_stripe_fee}
						amount={bid?.amount}
					/>
				) : bid ? (
					<Typography
						variant="body2"
						sx={{ px: { xs: 1.5, sm: 2 }, py: 2, fontWeight: 600 }}
					>
						Project total: {formatLineItemCurrency(bidAmount)}
					</Typography>
				) : null}
			</Box>

			<Box sx={surfaceCard(theme)}>
				<SectionHeader icon={AccountBalanceWalletIcon} title="Job Costs" />
				<Typography
					variant="body2"
					color="text.secondary"
					sx={{ px: { xs: 1.5, sm: 2 }, pt: 0.5, pb: 1 }}
				>
					Internal actual costs for this job — never shown on customer proposals
					or invoices.
					{marginLabel != null && bidAmount > 0
						? ` Margin vs current bid: ${marginLabel} (bid ${formatLineItemCurrency(bidAmount)} − costs ${formatLineItemCurrency(costsTotal)}).`
						: ''}
				</Typography>
				<Divider sx={{ opacity: 0.6 }} />
				<Box
					sx={{
						p: { xs: 1, sm: 1.5 },
						'& .MuiPaper-root': {
							border: 'none',
							boxShadow: 'none',
							bgcolor: 'transparent',
						},
					}}
				>
					<JobCostsEditor jobId={jobId} />
				</Box>
			</Box>

			<Box
				sx={{
					display: 'grid',
					gridTemplateColumns: {
						xs: '1fr',
						md: 'repeat(auto-fit, minmax(280px, 1fr))',
					},
					gap: { xs: 2.5, md: 3 },
					alignItems: 'stretch',
					'& > *': { minWidth: 0, height: '100%' },
				}}
			>
				<JobSchedulePanel job={selectedJob} onChanged={onJobUpdated} />
				<JobFiles jobId={jobId} />
			</Box>

			<ChangeOrderModal
				open={changeOrderOpen}
				onClose={() => setChangeOrderOpen(false)}
				jobId={jobId}
				onJobUpdated={onJobUpdated}
			/>
		</Stack>
	);
};

export default JobMainContent;
